import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Topic, Team } from '../types';
import { topicApi } from '../services/api.ts';
import { authService } from '../services/auth.ts';
import TopicRow from './TopicRow.tsx';
import EditTopicModal from './EditTopicModal.tsx';
import RatingModal from './RatingModal.tsx';
import NavigationMenu from './NavigationMenu.tsx';
import TruncatedDescription from './TruncatedDescription.tsx';
import PresentersList from './PresentersList.tsx';

interface PastTopicsPageProps {
  currentTeam: { teamId: string; teamName: string } | null;
  onBackToMain: () => void;
  onNavigateToMyTopicStats: () => void;
  onNavigateToDeletedTopics: () => void;
  onNavigateToAbout: () => void;
  onSubmitNewTopic: () => void;
}

const PastTopicsPage: React.FC<PastTopicsPageProps> = ({ currentTeam, onBackToMain, onNavigateToMyTopicStats, onNavigateToDeletedTopics, onNavigateToAbout, onSubmitNewTopic }) => {
  const navigate = useNavigate();
  const [topics, setTopics] = useState<Topic[]>([]);
  const [filteredTopics, setFilteredTopics] = useState<Topic[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [selectedTeam, setSelectedTeam] = useState<string>(currentTeam?.teamId || 'all');
  const [showTeamDropdown, setShowTeamDropdown] = useState(false);
  const [loadingTeams, setLoadingTeams] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<'title' | 'description' | 'submittedBy' | 'presentedAt' | 'teamName' | 'attendeeCount' | 'averageUsefulnessRating' | 'totalCustomerConversations'>('presentedAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [lastKey, setLastKey] = useState<string | null>(null);
  const [editingTopic, setEditingTopic] = useState<Topic | null>(null);
  const [ratingTopic, setRatingTopic] = useState<Topic | null>(null);
  const [stats, setStats] = useState<{
    averageRating: number;
    totalCustomers: number;
    averageAttendees: number;
    totalTopics: number;
    topTopics: Topic[];
  } | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [showDropdown, setShowDropdown] = useState<string | null>(null); // Track which topic's dropdown is open

  useEffect(() => {
    // Only reload data for server-side sortable fields
    const calculatedFields = ['totalHoursSaved', 'averageUsefulnessRating', 'totalCustomerConversations'];
    const shouldReload = !calculatedFields.includes(sortBy);
    
    if (shouldReload) {
      loadTeams();
      loadTopics(true);
      loadStats();
    }
  }, [selectedTeam, sortBy, sortOrder]);

  useEffect(() => {
    filterTopics();
  }, [topics, searchQuery]);

  useEffect(() => {
    const handleClickOutside = () => {
      if (showDropdown) {
        setShowDropdown(null);
      }
    };

    if (showDropdown) {
      document.addEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showDropdown]);

  const loadTeams = async () => {
    try {
      setLoadingTeams(true);
      const teams = await topicApi.getTeams();
      setTeams(teams);
    } catch (error) {
      console.error('Error loading teams:', error);
    } finally {
      setLoadingTeams(false);
    }
  };

  const loadStats = async () => {
    try {
      setStatsLoading(true);
      const statsData = await topicApi.getPastTopicsStats(selectedTeam);
      setStats(statsData);
    } catch (error) {
      console.error('Error loading stats:', error);
    } finally {
      setStatsLoading(false);
    }
  };

  const loadTopics = async (reset = false) => {
    try {
      setLoading(true);
      
      const teamId = selectedTeam === 'all' ? 'all' : selectedTeam;
      const keyToUse = reset ? undefined : lastKey;
      
      const data = await topicApi.getPastTopics(teamId, sortBy, sortOrder, 50, keyToUse);

      console.log('PastTopicsPage - loaded topics:', data.topics.map(t => ({ 
        title: t.title, 
        presenters: t.presenters 
      })));

      if (reset) {
        setTopics(data.topics || []);
      } else {
        setTopics(prev => [...prev, ...(data.topics || [])]);
      }
      
      setHasMore(data.hasMore || false);
      setLastKey(data.lastKey || null);
    } catch (error) {
      console.error('Error loading past topics:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterTopics = () => {
    let filtered = topics;
    
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = topics.filter(topic => {
        // Search in title and description
        const matchesBasic = topic.title.toLowerCase().includes(query) ||
          topic.description.toLowerCase().includes(query);
        
        // Search across all presenters (including hidden ones)
        const matchesPresenters = topic.presenters?.some(p => p.toLowerCase().includes(query));
        
        return matchesBasic || matchesPresenters;
      });
    }
    
    // Apply client-side sorting to filtered results
    const sorted = [...filtered].sort((a, b) => {
      let aVal, bVal;
      
      switch (sortBy) {
        case 'title':
          aVal = (a.title || '').toLowerCase();
          bVal = (b.title || '').toLowerCase();
          break;
        case 'description':
          aVal = (a.description || '').toLowerCase();
          bVal = (b.description || '').toLowerCase();
          break;
        case 'submittedBy':
          aVal = (a.presenters?.[0] || '').toLowerCase();
          bVal = (b.presenters?.[0] || '').toLowerCase();
          break;
        case 'teamName':
          aVal = (a.teamName || '').toLowerCase();
          bVal = (b.teamName || '').toLowerCase();
          break;
        case 'presentedAt':
          aVal = new Date(a.presentedAt || 0).getTime();
          bVal = new Date(b.presentedAt || 0).getTime();
          break;
        case 'attendeeCount':
          aVal = a.attendeeCount || 0;
          bVal = b.attendeeCount || 0;
          break;
        case 'averageUsefulnessRating':
          aVal = a.averageUsefulnessRating || 0;
          bVal = b.averageUsefulnessRating || 0;
          break;
        case 'totalCustomerConversations':
          aVal = a.totalCustomerConversations || 0;
          bVal = b.totalCustomerConversations || 0;
          break;
        default:
          return 0;
      }
      
      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
    
    setFilteredTopics(sorted);
  };

  const handleSort = (column: typeof sortBy) => {
    // For calculated fields, use client-side sorting
    const calculatedFields = ['totalHoursSaved', 'averageUsefulnessRating', 'totalCustomerConversations'];
    
    if (calculatedFields.includes(column)) {
      // Client-side sorting for calculated fields
      const newOrder = (sortBy === column && sortOrder === 'desc') ? 'asc' : 'desc';
      
      // Update state without triggering useEffect reload
      const sortedTopics = [...topics].sort((a, b) => {
        const aVal = a[column as keyof Topic] || 0;
        const bVal = b[column as keyof Topic] || 0;
        
        if (newOrder === 'asc') {
          return (aVal as number) - (bVal as number);
        } else {
          return (bVal as number) - (aVal as number);
        }
      });
      
      // Update topics and sort state separately to avoid useEffect trigger
      setTopics(sortedTopics);
      setSortBy(column);
      setSortOrder(newOrder);
      
      // Also update filtered topics
      const sortedFiltered = [...filteredTopics].sort((a, b) => {
        const aVal = a[column as keyof Topic] || 0;
        const bVal = b[column as keyof Topic] || 0;
        
        if (newOrder === 'asc') {
          return (aVal as number) - (bVal as number);
        } else {
          return (bVal as number) - (aVal as number);
        }
      });
      setFilteredTopics(sortedFiltered);
      
    } else {
      // Server-side sorting for database fields
      if (sortBy === column) {
        setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
      } else {
        setSortBy(column);
        setSortOrder('desc');
      }
    }
  };

  const handleTeamChange = (teamId: string) => {
    setSelectedTeam(teamId);
    setShowTeamDropdown(false);
    setTopics([]);
    setLastKey(null);
    
    // Update URL to reflect team change
    if (teamId !== 'all') {
      navigate(`/series/${teamId}/past-topics`);
    }
  };

  const handleTeamDropdownToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!showTeamDropdown) {
      loadTeams(); // Load teams when opening dropdown
    }
    setShowTeamDropdown(!showTeamDropdown);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showTeamDropdown) {
        setShowTeamDropdown(false);
      }
      if (showUserDropdown) {
        setShowUserDropdown(false);
      }
    };

    if (showTeamDropdown || showUserDropdown) {
      document.addEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showTeamDropdown, showUserDropdown]);

  const handleTopicUpdated = () => {
    loadTopics(true);
  };

  const getTeamName = (teamId: string) => {
    if (teamId === 'all') return 'All Series';
    const team = teams.find(t => t.teamId === teamId);
    return team?.teamName || 'Unknown Team';
  };

  const getSortIcon = (column: typeof sortBy) => {
    if (sortBy !== column) return '↕️';
    return sortOrder === 'asc' ? '↑' : '↓';
  };

  const handleNavigation = (page: 'main' | 'past-topics' | 'my-topic-stats' | 'deleted-topics' | 'submit-topic' | 'about') => {
    switch (page) {
      case 'main':
        onBackToMain();
        break;
      case 'my-topic-stats':
        onNavigateToMyTopicStats();
        break;
      case 'deleted-topics':
        onNavigateToDeletedTopics();
        break;
      case 'about':
        onNavigateToAbout();
        break;
      case 'submit-topic':
        onSubmitNewTopic();
        break;
    }
  };

  return (
    <div className="past-topics-page">
      <div className="header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1>ShareBrief</h1>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', position: 'relative' }}>
            <h2 
              onClick={handleTeamDropdownToggle}
              style={{ 
                margin: 0, 
                color: '#2563eb', 
                fontWeight: '600',
                fontSize: '1.5rem',
                cursor: 'pointer'
              }}
            >
              {getTeamName(selectedTeam)}
            </h2>
            <button
              onClick={handleTeamDropdownToggle}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                fontSize: '16px',
                color: '#666',
                padding: '2px'
              }}
            >
              {loadingTeams ? (
                <span className="loading-spinner">⟳</span>
              ) : (
                '▼'
              )}
            </button>
          {showTeamDropdown && (
            <div 
              onClick={(e) => e.stopPropagation()}
              style={{
                position: 'absolute',
                top: '100%',
                left: '0',
                backgroundColor: 'white',
                border: '1px solid #ccc',
                borderRadius: '4px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                zIndex: 1000,
                minWidth: '300px',
                marginTop: '4px'
              }}>
              <button
                onClick={() => handleTeamChange('all')}
                style={{
                  display: 'block',
                  width: '100%',
                  padding: '8px 12px',
                  border: 'none',
                  background: selectedTeam === 'all' ? '#f5f5f5' : 'none',
                  textAlign: 'left',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                All Series
              </button>
              {teams.map((team) => (
                <button
                  key={team.teamId}
                  onClick={() => handleTeamChange(team.teamId)}
                  style={{
                    display: 'block',
                    width: '100%',
                    padding: '8px 12px',
                    border: 'none',
                    background: selectedTeam === team.teamId ? '#f5f5f5' : 'none',
                    textAlign: 'left',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  {team.teamName}
                </button>
              ))}
            </div>
          )}
          </div>
        </div>
        
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
          {(() => {
            const userInfo = authService.getUserInfo();
            return userInfo ? (
              <div style={{ position: 'relative' }}>
                <div 
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowUserDropdown(!showUserDropdown);
                  }}
                  style={{ 
                    fontSize: '16px', 
                    marginBottom: '8px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}
                >
                  Welcome <span style={{ color: '#2563eb', fontWeight: '600' }}>{userInfo.name}</span>
                  <span style={{ fontSize: '12px', color: '#666' }}>▼</span>
                </div>
                
                {showUserDropdown && (
                  <div 
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      position: 'absolute',
                      top: '100%',
                      right: '0',
                      backgroundColor: 'white',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      zIndex: 9999,
                      minWidth: '120px'
                    }}
                  >
                    <button
                      onClick={() => {
                        authService.logout();
                      }}
                      style={{
                        display: 'block',
                        width: '100%',
                        padding: '8px 12px',
                        border: 'none',
                        background: 'none',
                        textAlign: 'left',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                      onMouseEnter={(e) => (e.target as HTMLElement).style.backgroundColor = '#f5f5f5'}
                      onMouseLeave={(e) => (e.target as HTMLElement).style.backgroundColor = 'transparent'}
                    >
                      Logout
                    </button>
                  </div>
                )}
              </div>
            ) : null;
          })()}
        </div>
      </div>
      
      <NavigationMenu 
        currentPage="past-topics" 
        currentTeamId={currentTeam?.teamId}
        onNavigate={handleNavigation}
      />

      {/* Statistics Cards */}
      <div className="stats-section" style={{ margin: '20px 0' }}>
        {statsLoading ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>Loading statistics...</div>
        ) : stats ? (
          <>
            {/* Metrics Cards */}
            <div className="metrics-cards" style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
              gap: '16px', 
              marginBottom: '24px' 
            }}>
              <div className="metric-card" style={{
                backgroundColor: '#f8fafc',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                padding: '20px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#1e40af' }}>
                  {stats.averageRating.toFixed(1)}/5 ⭐
                </div>
                <div style={{ fontSize: '16px', color: '#64748b', marginTop: '4px' }}>
                  Confidence Impact
                </div>
              </div>
              
              <div className="metric-card" style={{
                backgroundColor: '#e3f2fd',
                border: '1px solid #90caf9',
                borderRadius: '8px',
                padding: '20px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#1976d2' }}>
                  {stats.totalHoursSaved || 0} 🕒
                </div>
                <div style={{ fontSize: '16px', color: '#64748b', marginTop: '4px' }}>
                  Total Hours Saved
                </div>
              </div>
              
              <div className="metric-card" style={{
                backgroundColor: '#f0fdf4',
                border: '1px solid #bbf7d0',
                borderRadius: '8px',
                padding: '20px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#15803d' }}>
                  {stats.totalCustomers}
                </div>
                <div style={{ fontSize: '16px', color: '#64748b', marginTop: '4px' }}>
                  Total Customers Influenced
                </div>
              </div>
              
              <div className="metric-card" style={{
                backgroundColor: '#f0f9ff',
                border: '1px solid #7dd3fc',
                borderRadius: '8px',
                padding: '20px',
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#0369a1' }}>
                  {stats.totalTopics}
                </div>
                <div style={{ fontSize: '16px', color: '#64748b', marginTop: '4px' }}>
                  Topics Delivered
                </div>
              </div>
            </div>

            {/* Top Topics */}
            {stats.topTopics.length > 0 && (
              <div className="top-topics-section">
                <h3 style={{ marginBottom: '16px', color: '#1e293b' }}>🏆 Top 3 Topics</h3>
                <div className="top-topics-grid" style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                  gap: '16px'
                }}>
                  {stats.topTopics.map((topic, index) => (
                    <div key={topic.topicId} className="top-topic-card" style={{
                      backgroundColor: '#ffffff',
                      border: '2px solid #e2e8f0',
                      borderRadius: '8px',
                      padding: '16px',
                      position: 'relative'
                    }}>
                      <div style={{
                        position: 'absolute',
                        top: '-8px',
                        left: '16px',
                        backgroundColor: index === 0 ? '#fbbf24' : index === 1 ? '#9ca3af' : '#cd7c2f',
                        color: 'white',
                        padding: '6px 10px',
                        borderRadius: '4px',
                        fontSize: '16px',
                        fontWeight: 'bold'
                      }}>
                        #{index + 1}
                      </div>
                      
                      <h4 style={{ margin: '8px 0 8px 0', color: '#1e293b', fontSize: '16px' }}>{topic.title}</h4>
                      <p style={{ fontSize: '15px', color: '#64748b', marginBottom: '8px' }}>
                        <TruncatedDescription description={topic.description} topicId={topic.topicId} />
                      </p>
                      <div style={{ fontSize: '14px', color: '#64748b', marginBottom: '8px' }}>
                        <strong>Presenter(s):</strong> <PresentersList 
                          presenters={topic.presenters || []} 
                          currentUser={authService.getUserInfo()?.username}
                          displayMode="inline"
                        />
                      </div>
                      <div style={{ fontSize: '14px', color: '#64748b', marginBottom: '8px' }}>
                        <strong>Presented:</strong> {topic.presentedAt ? new Date(topic.presentedAt).toLocaleDateString('en-US', { 
                          year: 'numeric', 
                          month: 'long', 
                          day: 'numeric' 
                        }) : 'N/A'}
                      </div>
                      {selectedTeam === 'all' && (
                        <div style={{ fontSize: '14px', color: '#64748b', marginBottom: '8px' }}>
                          <strong>Series:</strong> {topic.teamName || 'Unknown Team'}
                        </div>
                      )}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                        <div style={{ fontSize: '15px' }}>
                          <span style={{ color: '#f59e0b' }}>
                            {topic.averageUsefulnessRating ? `${topic.averageUsefulnessRating.toFixed(1)}/5 ⭐` : 'No rating'}
                          </span>
                        </div>
                        <div style={{ fontSize: '15px', color: '#059669' }}>
                          {topic.totalCustomerConversations || 0} customers influenced
                        </div>
                        <div style={{ fontSize: '15px', color: '#1976d2' }}>
                          🕒 {topic.totalHoursSaved ? `${topic.totalHoursSaved.toFixed(1)} hrs saved` : '0 hrs saved'}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        ) : null}
      </div>

      {/* Divider */}
      <div style={{ 
        height: '1px', 
        backgroundColor: '#e2e8f0', 
        margin: '24px 0 20px 0' 
      }}></div>

      <div className="search-section">
        <input
          type="text"
          placeholder="Search topics by title, description, or presenter..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="search-input"
        />
      </div>

      <div className="topics-section">
        <table className="topics-table" style={{ overflow: 'visible' }}>
          <thead>
            <tr>
              <th onClick={() => handleSort('title')} className="sortable" style={{ minWidth: '350px' }}>
                Topic {getSortIcon('title')}
              </th>
              <th onClick={() => handleSort('submittedBy')} className="sortable">
                Presenter(s) {getSortIcon('submittedBy')}
              </th>
              <th onClick={() => handleSort('presentedAt')} className="sortable">
                Presented Date {getSortIcon('presentedAt')}
              </th>
              <th onClick={() => handleSort('teamName')} className="sortable">
                Presented To {getSortIcon('teamName')}
              </th>
              <th onClick={() => handleSort('attendeeCount')} className="sortable">
                Attendees {getSortIcon('attendeeCount')}
              </th>
              <th onClick={() => handleSort('averageUsefulnessRating')} className="sortable">
                Confidence Impact {getSortIcon('averageUsefulnessRating')}
              </th>
              <th onClick={() => handleSort('totalHoursSaved')} className="sortable">
                Prep Hours Saved {getSortIcon('totalHoursSaved')}
              </th>
              <th onClick={() => handleSort('totalCustomerConversations')} className="sortable">
                Customers Influenced {getSortIcon('totalCustomerConversations')}
              </th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody style={{ overflow: 'visible' }}>
            {filteredTopics.map((topic, index) => (
              <TopicRow
                key={topic.topicId}
                topic={topic}
                onTopicUpdate={handleTopicUpdated}
                showAllColumns={true}
                showAISummary={true}
                index={index}
                totalCount={filteredTopics.length}
                showDropdown={showDropdown === topic.topicId}
                onDropdownToggle={(topicId) => setShowDropdown(showDropdown === topicId ? null : topicId)}
              />
            ))}
          </tbody>
        </table>

        {loading && <div className="loading">Loading...</div>}
        
        {!loading && filteredTopics.length === 0 && (
          <div className="no-topics">
            {searchQuery ? 'No topics match your search.' : 'No past topics found.'}
          </div>
        )}

        {hasMore && !searchQuery && (
          <button 
            onClick={() => loadTopics(false)} 
            className="load-more-button"
            disabled={loading}
          >
            Load More Topics
          </button>
        )}
      </div>

      {editingTopic && (
        <EditTopicModal
          topic={editingTopic}
          onClose={() => setEditingTopic(null)}
          onTopicUpdated={handleTopicUpdated}
        />
      )}

      {ratingTopic && (
        <RatingModal
          topic={ratingTopic}
          onClose={() => setRatingTopic(null)}
        />
      )}
    </div>
  );
};

export default PastTopicsPage;
